import { useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import { AsyncBoundary, Icon, useToast } from '@kollez/design-system';
import {
  useApplication, useAddApplicationNote, useMoveApplication,
  useApplicationRun, useMatchExplain,
  type WorkflowRunHistoryEntry, type WorkflowRunDetail, type MatchExplainResult,
} from '@/lib/queries';
import { formatLPA, formatIndianDate, formatTime } from '@kollez/shared';

/**
 * /tpo/drives/:driveId/applications/:appId (F93b) — direct port of
 * kollez/pages/tpo/application.html.
 *   2-col layout · left tabs (Fit / Resume / All applications)
 *   right sticky aside (Stage select + timeline + Contact block)
 *   Fit: 4 scorebars (Required skills / Academics / Projects / Mock) + Nexie +/− reasons + Eligibility dl + Notes list w/ add-note
 *   Resume: .resume paper card w/ <mark> highlights
 *   History: drives table
 */
const STAGE_OPTIONS = ['Applied', 'Shortlisted', 'Assessment', 'Interview', 'Offered', 'Not this time'];
const STAGE_TO_STATUS: Record<string, string> = {
  Applied: 'Under_Review',
  Shortlisted: 'Shortlisted',
  Assessment: 'Assessment_Pending',
  Interview: 'Interview_Scheduled',
  Offered: 'Offered',
  'Not this time': 'Rejected',
};
function statusToStage(status: string): string {
  const s = status.toLowerCase();
  if (/reject|withdraw/.test(s)) return 'Not this time';
  if (/offer|accept/.test(s)) return 'Offered';
  if (/interview/.test(s)) return 'Interview';
  if (/test|assess/.test(s)) return 'Assessment';
  if (/short|hold/.test(s)) return 'Shortlisted';
  return 'Applied';
}
function initials(name?: string): string {
  return (name ?? '?').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

export function TpoApplicationDetailPage() {
  const { driveId, appId } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const app = useApplication(appId);
  const addNote = useAddApplicationNote(appId ?? '');
  const move = useMoveApplication();

  const [tab, setTab] = useState<'fit' | 'resume' | 'history'>('fit');
  const [noteText, setNoteText] = useState('');

  const a = app.data?.application;
  const sibs = app.data?.siblings;

  const stage = a ? statusToStage(a.status) : 'Applied';

  const scoreBars = useMemo(() => {
    if (!a?.matchScore) return [];
    const b = a.matchScore.breakdown;
    return [
      ['Required skills', pick(b, 'skill') ?? pick(b, 'skills') ?? a.matchScore.overall],
      ['Academics', pick(b, 'cgpa') ?? pick(b, 'academic') ?? 0],
      ['Projects', pick(b, 'project') ?? pick(b, 'projects') ?? 0],
      ['Mock interview', pick(b, 'mock') ?? pick(b, 'interview') ?? 0],
    ] as const;
  }, [a]);

  async function changeStage(s: string) {
    if (!a) return;
    try {
      await move.mutateAsync({ id: a._id, status: STAGE_TO_STATUS[s]! });
      toast.show({
        message: `Moved to ${s}${s === 'Not this time' ? ". Student is told when results publish." : '.'}`,
      });
    } catch { toast.show({ tone: 'warning', message: 'Move failed' }); }
  }

  async function submitNote() {
    if (noteText.trim().length < 3) return;
    try {
      await addNote.mutateAsync(noteText);
      setNoteText('');
    } catch { toast.show({ tone: 'warning', message: 'Could not add note' }); }
  }

  return (
    <AsyncBoundary
      kind="detail"
      loading={app.isLoading}
      error={app.error}
      empty={!app.isLoading && !a}
      emptyTitle="Application not found"
      onRetry={() => void app.refetch()}
    >
      {a && (
        <div className="stack">
          <style>{`
            .app-grid { display: grid; gap: 20px; grid-template-columns: minmax(0,1fr); align-items: start; }
            @media (min-width: 1100px) { .app-grid { grid-template-columns: minmax(0,1fr) 340px; } .app-side { position: sticky; top: 76px; } }
            .scorebar { display: grid; grid-template-columns: 130px minmax(0,1fr) 40px; gap: 10px; align-items: center; font-size: .875rem; }
            .note { border-left: 2px solid rgb(var(--line-secondary)); padding: 2px 0 2px 12px; }
            .resume { background: #fff; color: #1a1d2b; border: 1px solid rgb(var(--line-tertiary)); border-radius: 4px; padding: 28px 30px; font-size: 13px; line-height: 1.55; box-shadow: var(--shadow-sm); }
            .resume h4 { font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: #5b6070; margin: 14px 0 4px; }
            .resume mark { background: rgb(31 64 175 / .12); color: inherit; border-radius: 2px; padding: 0 2px; }
          `}</style>

          {/* Header */}
          <div className="page-header">
            <div className="flex items-center gap-4 min-w-0">
              <span className="avatar avatar-xl">{initials(a.student?.fullName)}</span>
              <div className="ph-text">
                <p className="eyebrow">
                  {a.drive?.partner?.name ?? '—'} ·{' '}
                  {(() => {
                    const roles = (a.drive as unknown as { roles?: Array<{ opportunity?: { _id?: string; title?: string } }> })?.roles ?? [];
                    const appOppId = String(a.opportunity ?? '');
                    const role = roles.find((r) => String(r.opportunity?._id ?? '') === appOppId) ?? roles[0];
                    return role?.opportunity?.title ?? a.drive?.opportunity?.title ?? '—';
                  })()}
                  {' · applied '}{formatIndianDate(a.appliedAt)}
                </p>
                <h1 className="h-page">{a.student?.fullName ?? '—'}</h1>
                <p className="subhead">
                  {a.student?.branch ?? '—'} · {a.student?.rollNumber ?? '—'} · CGPA {a.student?.cgpa?.toFixed(2) ?? '—'}
                  {' · '}{a.student?.backlogs === 0 ? 'no backlogs' : `${a.student?.backlogs} backlogs`}
                </p>
              </div>
            </div>
            <div className="ph-actions">
              <button
                className="btn btn-ghost btn-icon"
                aria-label="Previous applicant"
                disabled={!sibs?.prev}
                onClick={() => sibs?.prev && nav(`/tpo/drives/${driveId}/applications/${sibs.prev}`)}
              >
                <Icon name="chevron-left" size={14} />
              </button>
              <span className="helper">{sibs ? `${sibs.index + 1} of ${sibs.total}` : ''}</span>
              <button
                className="btn btn-ghost btn-icon"
                aria-label="Next applicant"
                disabled={!sibs?.next}
                onClick={() => sibs?.next && nav(`/tpo/drives/${driveId}/applications/${sibs.next}`)}
              >
                <Icon name="chevron-right" size={14} />
              </button>
            </div>
          </div>

          <div className="app-grid">
            <div className="grid gap-5 min-w-0">
              <div className="tabs" role="tablist">
                <button className="tab" role="tab" aria-selected={tab === 'fit'} onClick={() => setTab('fit')}>Fit</button>
                <button className="tab" role="tab" aria-selected={tab === 'resume'} onClick={() => setTab('resume')}>Resume</button>
                <button className="tab" role="tab" aria-selected={tab === 'history'} onClick={() => setTab('history')}>
                  All applications
                </button>
              </div>

              {tab === 'fit' && (
                <section className="grid gap-5">
                  {a.matchScore && (
                    <div className="card card-pad grid gap-4">
                      <div className="flex items-baseline justify-between">
                        <h2 className="h-section">Why {a.matchScore.overall}% match</h2>
                        <span className="helper">Nexie · from resume and academics</span>
                      </div>
                      <div className="grid gap-3">
                        {scoreBars.map(([label, v]) => {
                          const val = typeof v === 'number' ? Math.round(v) : 0;
                          const low = val < 70;
                          return (
                            <div key={label} className="scorebar">
                              <span>{label}</span>
                              <div className={`bar${low ? ' warning' : ''}`}>
                                <span style={{ width: `${Math.min(100, val)}%` }} />
                              </div>
                              <b>{val}</b>
                            </div>
                          );
                        })}
                      </div>
                      {a.matchScore.reasoning && (
                        <p className="text-sm text-ink-secondary">{a.matchScore.reasoning}</p>
                      )}
                      <ul className="grid gap-2 m-0 p-0 list-none text-sm">
                        {(a.student?.skills ?? []).slice(0, 3).map((s) => (
                          <li key={s} className="flex gap-2">
                            <Icon name="plus" size={12} className="mt-0.5" style={{ color: 'rgb(var(--ink-success))', flexShrink: 0 }} />
                            {s} listed on the resume — matches the role's core stack.
                          </li>
                        ))}
                        {(a.drive?.opportunity?.eligibility?.branches ?? []).length > 0 && (
                          <li className="flex gap-2">
                            <Icon name="minus" size={12} className="mt-0.5" style={{ color: 'rgb(var(--ink-warning))', flexShrink: 0 }} />
                            Verify database exposure — the company's test typically has a DB section.
                          </li>
                        )}
                      </ul>
                      <p className="helper m-0">Nexie suggests; it doesn't decide. Check the resume before moving them.</p>
                    </div>
                  )}

                  <NexieMatchExplainCard applicationId={a._id} />

                  <WorkflowRunCard applicationId={a._id} />

                  <div className="card card-pad grid gap-3">
                    <h2 className="h-section">Eligibility</h2>
                    <dl className="dl">
                      <dt>Branch</dt>
                      <dd>
                        {a.student?.branch ?? '—'}
                        {(() => {
                          const roles = (a.drive as unknown as { roles?: Array<{ opportunity: { _id?: string; eligibility?: { branches?: string[] } } }> })?.roles ?? [];
                          const appOppId = String(a.opportunity ?? '');
                          const role = roles.find((r) => String(r.opportunity?._id ?? '') === appOppId) ?? roles[0];
                          const branches = role?.opportunity?.eligibility?.branches ?? a.drive?.opportunity?.eligibility?.branches ?? [];
                          if (branches.includes(a.student?.branch ?? '')) {
                            return <span className="badge badge-success ml-1">OK</span>;
                          }
                          return null;
                        })()}
                      </dd>
                      <dt>CGPA</dt>
                      <dd>
                        {a.student?.cgpa?.toFixed(2) ?? '—'}
                        {(() => {
                          // Drive now hosts N roles; find the one matching this Application's
                          // opportunity for the eligibility read.
                          const roles = (a.drive as unknown as { roles?: Array<{ opportunity: { _id?: string; eligibility?: { minCGPA?: number } } }> })?.roles ?? [];
                          const appOppId = String(a.opportunity ?? '');
                          const role = roles.find((r) => String(r.opportunity?._id ?? r.opportunity) === appOppId) ?? roles[0];
                          const minCGPA = role?.opportunity?.eligibility?.minCGPA;
                          if (typeof minCGPA !== 'number') return null;
                          return (
                            <> · needs {minCGPA}
                              {(a.student?.cgpa ?? 0) >= minCGPA && (
                                <span className="badge badge-success ml-1">OK</span>
                              )}
                            </>
                          );
                        })()}
                      </dd>
                      <dt>10th / 12th</dt>
                      <dd>
                        {a.student?.class10Marks ?? '—'}% / {a.student?.class12Marks ?? '—'}%
                        <span className="badge badge-success ml-1">OK</span>
                      </dd>
                      <dt>Backlogs</dt>
                      <dd>
                        {a.student?.backlogs === 0 ? 'None' : `${a.student?.backlogs} active`}
                        {(a.student?.backlogs ?? 0) === 0 && <span className="badge badge-success ml-1">OK</span>}
                      </dd>
                    </dl>
                  </div>

                  <div className="card card-pad grid gap-3">
                    <div className="flex items-center justify-between">
                      <h2 className="h-section">Notes</h2>
                      <span className="helper">Only placement staff see these</span>
                    </div>
                    {(a.notes ?? []).length === 0 && (
                      <p className="text-sm text-ink-tertiary m-0">No notes yet.</p>
                    )}
                    {(a.notes ?? []).map((n) => (
                      <div key={n._id} className="note">
                        <div className="text-sm">{n.text}</div>
                        <div className="cell-sub">{n.author} · {formatIndianDate(n.at)}</div>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        className="input"
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') submitNote(); }}
                        placeholder="Add a note"
                      />
                      <button className="btn btn-outline" onClick={submitNote} disabled={addNote.isPending || noteText.trim().length < 3}>
                        Add
                      </button>
                    </div>
                  </div>
                </section>
              )}

              {tab === 'resume' && (
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <span className="helper">
                      Resume · <mark style={{ background: 'rgb(var(--bg-info))', padding: '0 4px', borderRadius: 3 }}>highlighted</mark> = matches the role
                    </span>
                    {a.student?.resumeUrl && (
                      <a className="btn btn-outline btn-sm" href={a.student.resumeUrl} target="_blank" rel="noopener noreferrer">
                        <Icon name="download" size={14} />PDF
                      </a>
                    )}
                  </div>
                  <div className="resume">
                    <div style={{ fontFamily: 'Newsreader, Georgia, serif', fontSize: 22 }}>{a.student?.fullName ?? '—'}</div>
                    <div style={{ color: '#5b6070' }}>
                      {a.student?.email ?? '—'} · +91 {a.student?.phone ?? '••••• •••••'} · {a.student?.branch ?? '—'}
                    </div>
                    <h4>Education</h4>
                    <div>
                      B.Tech {a.student?.branch ?? ''} · CGPA {a.student?.cgpa?.toFixed(2) ?? '—'}
                    </div>
                    {a.student?.skills && a.student.skills.length > 0 && (
                      <>
                        <h4>Skills</h4>
                        <div>{highlightSkills(a.student.skills.join(', '), a.student.skills)}</div>
                      </>
                    )}
                    {a.student?.resumeText && (
                      <>
                        <h4>Details</h4>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{a.student.resumeText}</div>
                      </>
                    )}
                  </div>
                </section>
              )}

              {tab === 'history' && (
                <section className="card">
                  <div className="tbl-wrap">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Drive</th>
                          <th>Stage</th>
                          <th className="hide-mobile">Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="cell-title">
                            {a.drive?.driveName ?? '—'}
                            {typeof a.drive?.tier === 'number' && (
                              <span className={`tier${a.drive.tier === 0 ? ' tier-0' : ''} ml-1`}>Day {a.drive.tier}</span>
                            )}
                          </td>
                          <td><span className="badge badge-info">{stage}</span></td>
                          <td className="hide-mobile cell-sub">{formatIndianDate(a.appliedAt)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </div>

            <aside className="app-side grid gap-4">
              <div className="card card-pad grid gap-3">
                <p className="eyebrow">Stage</p>
                <select className="select" value={stage} onChange={(e) => changeStage(e.target.value)}>
                  {STAGE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                </select>
                <ol className="timeline mt-2">
                  {(a.statusHistory ?? [{ status: a.status, at: a.appliedAt }]).map((h, i, arr) => (
                    <li key={i} data-state={i === arr.length - 1 ? 'current' : 'done'}>
                      <span className="t-dot" />
                      <div className="t-title">{statusToStage(h.status)}</div>
                      <div className="t-meta">
                        {formatIndianDate(h.at)}, {formatTime(h.at)}
                        {h.by ? ` · by ${h.by}` : ''}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="card card-pad grid gap-2">
                <p className="eyebrow">Contact</p>
                {a.student?.phone ? (
                  <a
                    className="btn btn-outline justify-start"
                    href={`https://wa.me/${String(a.student.phone).replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon name="message-circle" size={14} />WhatsApp
                  </a>
                ) : (
                  <button className="btn btn-outline justify-start" type="button" disabled title="No phone on record">
                    <Icon name="message-circle" size={14} />WhatsApp
                  </button>
                )}
                {a.student?.phone ? (
                  <a className="btn btn-outline justify-start" href={`tel:+91${a.student.phone}`}>
                    <Icon name="phone" size={14} />+91 {a.student.phone}
                  </a>
                ) : (
                  <button className="btn btn-outline justify-start" disabled><Icon name="phone" size={14} />No phone on file</button>
                )}
                {a.student?.mentor?.fullName && (
                  <p className="helper m-0">Mentor: {a.student.mentor.fullName}</p>
                )}
              </div>
              <Link className="btn btn-ghost justify-self-start" to={`/tpo/drives/${driveId}`}>← Back to drive</Link>
            </aside>
          </div>
        </div>
      )}
    </AsyncBoundary>
  );
}

// F-088: `matchScore.breakdown` is a plain object (`{branchFit, cgpaVsCutoff, ...}`) in the API
// response, but this page was written assuming an array of `{dimension, score}` — the mismatch
// crashed the whole page with `breakdown.find is not a function`. Accept both shapes.
function pick(
  breakdown: Array<{ dimension: string; score: number }> | Record<string, number> | undefined | null,
  key: string,
): number | undefined {
  if (!breakdown) return undefined;
  if (Array.isArray(breakdown)) {
    return breakdown.find((b) => b.dimension?.toLowerCase().includes(key))?.score;
  }
  // Object shape — match keys case-insensitively including the target substring.
  const lower = key.toLowerCase();
  for (const [k, v] of Object.entries(breakdown)) {
    if (k.toLowerCase().includes(lower) && typeof v === 'number') return v;
  }
  return undefined;
}

function highlightSkills(text: string, skills: string[]): React.ReactNode {
  if (!skills.length) return text;
  const parts: React.ReactNode[] = [];
  const set = new Set(skills.map((s) => s.toLowerCase()));
  const tokens = text.split(/([,\s]+)/);
  tokens.forEach((t, i) => {
    if (set.has(t.trim().toLowerCase())) parts.push(<mark key={i}>{t}</mark>);
    else parts.push(t);
  });
  return parts;
}

/**
 * v1.6 §7.6 WorkflowRun timeline card. Shows the current stage on the run's frozen
 * workflow version, any auto-satisfied stages (skipped via existing badges), and the full
 * transition history with author, note, satisfying-badge ref, and monetization txn ref.
 *
 * Renders nothing when the Application has no run yet (pre-v1.6 rows / missing template
 * on the Opportunity). Not an error state — TPO shouldn't see red for a well-known gap.
 */
function WorkflowRunCard({ applicationId }: { applicationId: string }) {
  const q = useApplicationRun(applicationId);
  const run = q.data?.run ?? null;

  if (q.isLoading) {
    return (
      <div className="card card-pad grid gap-2">
        <h2 className="h-section">Workflow run</h2>
        <p className="helper m-0">Loading…</p>
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="card card-pad grid gap-2">
        <h2 className="h-section">Workflow run</h2>
        <p className="helper m-0">Could not load the run for this application.</p>
      </div>
    );
  }
  if (!run) {
    return (
      <div className="card card-pad grid gap-2">
        <h2 className="h-section">Workflow run</h2>
        <p className="helper m-0">
          No workflow attached yet. Ask the recruiter to pick a template on the opening —
          Applications on template-less openings save fine but skip the run projection.
        </p>
      </div>
    );
  }

  const workflowName =
    typeof run.workflow === 'object' && run.workflow ? run.workflow.name : 'template';
  const workflowStages =
    typeof run.workflow === 'object' && run.workflow?.stages ? run.workflow.stages : [];
  const currentStageDef = workflowStages.find((s) => s.key === run.currentStage);

  return (
    <div className="card card-pad grid gap-3">
      <div className="flex items-center justify-between">
        <h2 className="h-section">Workflow run</h2>
        <span className={`badge ${run.status === 'active' ? 'badge-success' : run.status === 'flagged' ? 'badge-warning' : ''}`}>
          {run.status}
        </span>
      </div>

      <div className="cell-sub">
        <b>{workflowName}</b>
        {typeof run.workflow === 'object' && run.workflow ? <> · v{run.workflow.version} · {run.workflow.scope}</> : null}
      </div>

      <div>
        <div className="cell-sub">Current stage</div>
        <div className="flex items-center gap-2">
          <b>{currentStageDef?.label ?? run.currentStage}</b>
          <span className="tag">{run.currentStage}</span>
          {currentStageDef ? <span className="tag">{currentStageDef.reversibilityClass}</span> : null}
        </div>
      </div>

      {run.autoSatisfiedStages.length > 0 ? (
        <div>
          <div className="cell-sub">Auto-satisfied via existing badges</div>
          <div className="flex flex-wrap gap-1">
            {run.autoSatisfiedStages.map((s) => <span key={s} className="tag tag-pressed">{s}</span>)}
          </div>
        </div>
      ) : null}

      <div>
        <div className="cell-sub mb-1">History ({run.history.length})</div>
        <ol className="grid gap-1 text-sm">
          {run.history.map((h, i) => <HistoryRow key={i} entry={h} />)}
        </ol>
      </div>
    </div>
  );
}

function HistoryRow({ entry }: { entry: WorkflowRunHistoryEntry }) {
  const by = entry.by?.fullName ?? (entry.auto ? 'auto' : '—');
  const satisfy = typeof entry.satisfiedByBadge === 'object' ? entry.satisfiedByBadge : null;
  const emit = typeof entry.emittedBadge === 'object' ? entry.emittedBadge : null;
  return (
    <li className="note">
      <div className="text-sm">
        <b>{entry.fromStage}</b> → <b>{entry.toStage}</b>
        {entry.auto ? <span className="badge ml-1">auto</span> : null}
        {entry.satisfiedByBadge ? <span className="badge badge-success ml-1">badge-satisfied</span> : null}
        {entry.emittedBadge ? <span className="badge badge-info ml-1">badge-emitted</span> : null}
        {entry.monetizationTxn ? <span className="badge ml-1">charged</span> : null}
      </div>
      {satisfy ? (
        <div className="cell-sub">
          satisfied by <b>{satisfy.skill}</b>
          {satisfy.level ? ` (${satisfy.level})` : ''}
          {satisfy.issuer?.name ? ` · issued by ${satisfy.issuer.name}` : ''}
          {satisfy.issuedAt ? ` · ${formatIndianDate(satisfy.issuedAt)}` : ''}
          {satisfy.certified ? ' · certified' : ''}
        </div>
      ) : null}
      {emit ? (
        <div className="cell-sub">
          awarded <b>{emit.skill}</b>
          {emit.level ? ` (${emit.level})` : ''}
          {emit.certified ? ' · certified' : ''}
        </div>
      ) : null}
      {entry.note ? <div className="cell-sub">{entry.note}</div> : null}
      <div className="cell-sub">{by} · {formatIndianDate(entry.at)}</div>
    </li>
  );
}

// Type re-export so the section imports resolve.
export type { WorkflowRunDetail };

/**
 * v1.6 §7.9 / F34 — Nexie match-explain card. Deferred (on-demand) so the TPO can trigger
 * the LLM call themselves; not every application detail needs a paid Nexie call. The moment
 * the TPO wants a why-this-match narrative, they click and the card fills in.
 *
 * Renders: 2-paragraph narrative + 3 grounded bullets + up-to-2 concerns + a citations strip
 * (StudentProfile / Opportunity / Badge references). Every claim ties back to a citation the
 * TPO can inspect. This is what makes the AI defensible in placement meetings.
 */
function NexieMatchExplainCard({ applicationId }: { applicationId: string }) {
  const explain = useMatchExplain();
  const [result, setResult] = useState<MatchExplainResult | null>(null);
  const toast = useToast();

  async function run() {
    try {
      const res = await explain.mutateAsync(applicationId);
      setResult(res.explain);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Nexie could not explain this match.';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  if (!result) {
    return (
      <div className="card card-pad grid gap-2">
        <div className="flex items-center justify-between">
          <h2 className="h-section">Why this match, in words</h2>
          <button
            className="btn btn-primary btn-sm"
            onClick={run}
            disabled={explain.isPending}
          >
            {explain.isPending ? 'Nexie is reading…' : 'Ask Nexie'}
          </button>
        </div>
        <p className="helper m-0">
          Deterministic score above is already computed. Nexie turns it into 3 grounded bullets
          + concerns + citations you can share with the placement committee.
        </p>
      </div>
    );
  }

  return (
    <div className="card card-pad grid gap-3">
      <div className="flex items-center justify-between">
        <h2 className="h-section">Why this match, in words</h2>
        <button
          className="btn btn-ghost btn-sm"
          onClick={run}
          disabled={explain.isPending}
        >
          {explain.isPending ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <p className="text-sm m-0" style={{ whiteSpace: 'pre-wrap' }}>{result.narrative}</p>

      {result.bullets.length > 0 ? (
        <ul className="grid gap-1 m-0 p-0 list-none text-sm">
          {result.bullets.map((b, i) => (
            <li key={i} className="cell-sub">· {b}</li>
          ))}
        </ul>
      ) : null}

      {result.concerns.length > 0 ? (
        <div className="notice notice-warning">
          <Icon name="info" size={14} />
          <div>
            <b>Check before moving them:</b>
            <ul className="m-0 pl-4">
              {result.concerns.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        </div>
      ) : null}

      {result.citations.length > 0 ? (
        <div className="grid gap-1">
          <div className="cell-sub" style={{ fontSize: 11 }}>Nexie read:</div>
          <div className="flex flex-wrap gap-1">
            {result.citations.map((c, i) =>
              c.href ? (
                <Link
                  key={i}
                  to={c.href}
                  className="tag"
                  title={c.snippet ?? ''}
                  style={{ fontSize: 11 }}
                >
                  {c.sourceType}: {c.label} ↗
                </Link>
              ) : (
                <span
                  key={i}
                  className="tag"
                  title={c.snippet ?? ''}
                  style={{ fontSize: 11 }}
                >
                  {c.sourceType}: {c.label}
                </span>
              ),
            )}
          </div>
        </div>
      ) : null}

      <p className="helper m-0">Nexie explains; you decide. Every claim ties back to a citation above.</p>
    </div>
  );
}

